// whoIsItFor.js — the "Who is it for" search facet's axis rows, as a pure function.
//
// The design handoff (design/desk-search, turns 6a–6d) replaces the Terms facet's flat wall
// of ~37 pills with collapsed AXIS rows: the axis name, its answer or `not asked`, and the
// pills only inside the axis you opened. Its one structural rule: **the grouping is a query,
// never a second taxonomy.** The flat filter codes already know which axis they belong to —
// `restriction_code_projection` (0398/0403) maps every pol_* code to its predicate, and the
// predicate's keys ARE the axes. That table now rides GET /api/core/restriction-vocab as
// `projections`, so this module derives the rows and hardcodes nothing.
//
// ⚠️ feature.subcategory is the OLD grouping (nationality_macro, tenant_type, condition…)
// and must not gain a new consumer — the README warns the mock's own nine row names were
// inferred from it and are wrong where they disagree with 0398 (there is no religion axis;
// `muslim` is a group VALUE of nationality).
//
// Row set, derived:
//   • one row per 0398 axis that has at least one active flat code, in the axis sort_order
//   • pet_species + pet_size MERGE into one "pets" row (a small dog is one animal, not two
//     facts about a flat — the same scope rule the capture model runs on), positioned at
//     pet_species' slot and labelled with its name
//   • the two listing-level markers (terms_no_limits / terms_standard) form a trailing
//     `standard` row — they describe the AD's restriction posture, not a person
//   • an active pol_ code with no projection lands in a final `other` row rather than
//     vanishing: a filter silently dropped from the panel is the failure mode this whole
//     rework exists to prevent. Empty today; the row renders only if it has codes.
//
// This module is UI-free and node-testable: node --test src/lib/whoIsItFor.test.mjs

const STANDARD_CODES = new Set(['terms_no_limits', 'terms_standard'])

// Within a row, the broad umbrella code leads (pol_animals before pol_cat — its predicate
// names several values, or a group value), then vocabulary order by Georgian name. The
// mock's own Pets row is ordered this way; it is a readability rule, not semantics.
function rowSort(a, b) {
  if (a.broad !== b.broad) return a.broad ? -1 : 1
  return String(a.label).localeCompare(String(b.label), 'ka')
}

// features: active tenant_terms rows (caller filters `active` — the same rule the flat
// facet applies). vocab: the /restriction-vocab payload ({axes, projections, group_values}).
// Returns [{ key, label, codes: [{code, label, broad}] }] — rows with no codes are absent.
export function whoIsItForRows(features, vocab) {
  const axes = Array.isArray(vocab?.axes) ? vocab.axes : []
  const projections = Array.isArray(vocab?.projections) ? vocab.projections : []
  const groupVals = new Set((vocab?.group_values || []).map(g => `${g.axis_code}|${g.value}`))
  const predOf = new Map(projections.map(p => [p.feature_code, p.predicate || {}]))

  // Which row does a predicate live on? Any pet_* key → the merged pets row; otherwise the
  // predicate's (single, for flat codes) axis key.
  const rowKeyOf = (pred) => {
    const keys = Object.keys(pred || {})
    if (!keys.length) return null
    if (keys.some(k => k.startsWith('pet_'))) return 'pets'
    return keys[0]
  }

  const rows = new Map()   // key → { key, label, codes: [] }
  const ensure = (key, label) => {
    if (!rows.has(key)) rows.set(key, { key, label, codes: [] })
    return rows.get(key)
  }

  for (const f of features || []) {
    const label = f.name_ka || f.name_en || f.code
    if (STANDARD_CODES.has(f.code)) {
      ensure('standard', null).codes.push({ code: f.code, label, broad: false })
      continue
    }
    const pred = predOf.get(f.code)
    const key = pred ? rowKeyOf(pred) : null
    if (!key) {
      ensure('other', null).codes.push({ code: f.code, label, broad: false })
      continue
    }
    // Broad = the code stands for a set: several values on one axis (pol_animals,
    // pol_rus_ukr_bel) or a declared group value (foreigner, muslim, european…).
    const broad = Object.entries(pred).some(([ax, v]) =>
      Array.isArray(v) && (v.length > 1 || v.some(val => groupVals.has(`${ax}|${val}`))))
    ensure(key, null).codes.push({ code: f.code, label, broad })
  }

  // Order: axes by their declared sort_order (pets sits at pet_species' slot), then
  // `standard`, then `other`. Labels come from the axis vocabulary — one vocabulary,
  // two renderings, exactly like the capture builder.
  const orderOf = (key) => {
    if (key === 'standard') return 10000
    if (key === 'other') return 10001
    const ax = axes.find(a => a.code === (key === 'pets' ? 'pet_species' : key))
    return ax ? ax.sort_order : 9000
  }
  const labelOf = (key) => {
    if (key === 'standard' || key === 'other') return null   // i18n keys, caller supplies
    const ax = axes.find(a => a.code === (key === 'pets' ? 'pet_species' : key))
    return ax ? (ax.name_ka || ax.name_en || key) : key
  }

  return [...rows.values()]
    .map(r => ({ ...r, label: labelOf(r.key), codes: [...r.codes].sort(rowSort) }))
    .sort((a, b) => orderOf(a.key) - orderOf(b.key))
}
